'use client'

import { useMemo, useRef } from 'react'
import useSWR from 'swr'
import Link from 'next/link'
import { motion, useInView } from 'motion/react'
import { ShoppingCart, Star, PackageX, ArrowUpRight } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ImageWithFallback } from './ImageWithFallback'
import type { Product } from './types'
import { getBestSellers, mapProduct } from '@/lib/shop/api'
import { useDefaultCurrency, formatCurrency } from '@/lib/useDefaultCurrency'
import { useLanguage } from '@/components/i18n/LanguageProvider'
import fr from '@/i18n/fr.json'
import en from '@/i18n/en.json'

type ModernProductsProps = {
  onAddToCart: (product: Product) => void
}

export function ModernProducts({ onAddToCart }: ModernProductsProps) {
  const { currency: defaultCurrency } = useDefaultCurrency()
  const { currencyCode, language } = useLanguage()
  const copy = language === 'fr' ? fr : en
  const { data, isLoading, error } = useSWR(['best-sellers', currencyCode], async () => {
    const res = await getBestSellers(6, currencyCode)
    if (res.ok && Array.isArray((res.body as any)?.products)) {
      return (res.body as any).products.map(mapProduct) as Product[]
    }
    return []
  })

  const featuredProducts = useMemo(() => {
    if (data) return data
    return []
  }, [data, error])

  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '-100px' })

  const handleAddToCart = (product: Product) => {
    onAddToCart(product)
    const weightPart = product.weight ? `${product.weight} - ` : ''
    toast.success(`${product.name} ajouté au panier`, {
      description: `${weightPart}${formatCurrency(product.price, { code: defaultCurrency?.code || product.currency, symbol: defaultCurrency?.symbol })}`,
    })
  }

  return (
    <section id="products" ref={ref} className="bg-background py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div className="mb-16 text-center" initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6 }}>
          <Badge variant="outline" className="mb-4 px-4 py-2">
            {copy.products.badge}
          </Badge>
          <h2 className="mb-6 text-4xl tracking-tight sm:text-5xl lg:text-6xl">{copy.products.title}</h2>
          <p className="mx-auto max-w-2xl text-xl text-muted-foreground">{copy.products.subtitle}</p>
        </motion.div>

        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
          {featuredProducts.map((product, index) => (
            <motion.div key={product.id} initial={{ opacity: 0, y: 30 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: index * 0.1 }}>
              <article
                className={
                  'group relative flex h-full flex-col overflow-hidden rounded-2xl border border-border/60 bg-card transition-all duration-500 ' +
                  'hover:border-primary/30 hover:shadow-[0_8px_40px_-12px_rgba(0,0,0,0.25)] ' +
                  (!product.available ? 'opacity-75 grayscale-[0.3]' : '')
                }
              >
                {/* Image */}
                <div className="relative aspect-[4/5] overflow-hidden bg-gradient-to-b from-muted/40 to-muted/80">
                  <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-primary/5 via-transparent to-transparent" />
                  <motion.div className="absolute inset-0" whileHover={{ scale: 1.05 }} transition={{ duration: 0.6 }}>
                    <ImageWithFallback src={product.image} alt={product.name} className="h-full w-full object-contain p-6" />
                  </motion.div>

                  {/* Badge */}
                  <div className="absolute left-3 top-3 flex items-center space-x-1 rounded-md bg-card/90 px-2.5 py-1 text-xs font-medium backdrop-blur-sm">
                    <Star className="h-3 w-3 fill-secondary text-secondary" />
                    <span>{product.bestSeller ? copy.products.bestSeller : copy.products.new}</span>
                  </div>

                  {/* Out of stock */}
                  {!product.available && (
                    <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/30 backdrop-blur-[2px]">
                      <div className="flex items-center gap-2 rounded-lg bg-destructive/90 px-4 py-2 text-sm font-semibold text-destructive-foreground shadow-lg">
                        <PackageX className="h-4 w-4" />
                        Rupture de stock
                      </div>
                    </div>
                  )}

                  <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-card to-transparent" />
                </div>

                {/* Content */}
                <div className="flex flex-1 flex-col gap-3 p-4 pt-2">
                  <div className="space-y-1">
                    <Link href={`/product/${product.slug}`} className="block">
                      <h3 className="text-[15px] font-semibold leading-snug tracking-tight text-foreground transition-colors group-hover:text-primary">
                        {product.name}
                      </h3>
                    </Link>
                    {product.origin && (
                      <p className="text-xs text-muted-foreground">
                        <span className="inline-block h-1 w-1 rounded-full bg-primary/70 align-middle" /> {product.origin}
                      </p>
                    )}
                  </div>

                  <p className="text-xs leading-relaxed text-muted-foreground/80 line-clamp-2">{product.description}</p>

                  <div className="mt-auto border-t border-border/40 pt-3">
                    <div className="flex items-end justify-between gap-3">
                      <div>
                        {product.weight && (
                          <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/70">{product.weight}</p>
                        )}
                        <p className="text-xl font-bold tracking-tight text-primary">
                          {formatCurrency(product.price, { code: defaultCurrency?.code || product.currency, symbol: defaultCurrency?.symbol })}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button asChild variant="ghost" size="sm" className="h-9 rounded-lg px-3 text-xs font-medium text-muted-foreground hover:text-primary">
                          <Link href={`/product/${product.slug}`} className="flex items-center gap-1">
                            {copy.products.view}
                            <ArrowUpRight className="h-3 w-3" />
                          </Link>
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleAddToCart(product)}
                          disabled={!product.available}
                          className="h-9 rounded-lg px-4 text-xs font-semibold bg-primary text-primary-foreground hover:bg-primary/90 hover:shadow-md hover:shadow-primary/20 disabled:opacity-40"
                        >
                          <ShoppingCart className="mr-1.5 h-3.5 w-3.5" />
                          {copy.products.add}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </article>
            </motion.div>
          ))}
          {!isLoading && featuredProducts.length === 0 && (
            <div className="col-span-full rounded-2xl border border-dashed border-primary/40 bg-primary/5 p-6 text-center text-sm text-muted-foreground">
              {copy.products.empty}
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
